// TextBotDetail.jsx — Phase 5: AI Text Bots
// 4 tabs: Scripts | Examples | Personality | Test & Feedback

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MessageSquare, ArrowLeft, FileText, BookOpen, Sliders, TestTube2,
  Plus, Trash2, Loader2, Star, ChevronRight, Send, Lightbulb,
  CheckCircle2, AlertCircle, Zap, ZapOff, Save, Tag, X, RotateCcw,
  Upload, Download, ThumbsUp, ThumbsDown,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { apiGet, apiPost, apiPut, apiDel } from '../lib/api.js';
import TopBar from '../components/TopBar.jsx';

// ── Helpers ─────────────────────────────────────────────────────────────────

// PR #620 — minimal RFC-4180 CSV parser (no new dep).
// Handles quoted fields, escaped double-quotes (""), embedded commas,
// embedded newlines inside quotes, and both CRLF/LF line endings.
// Returns Array<Array<string>> (row-major, header row is rows[0]).
function parseCSV(text) {
  if (typeof text !== 'string' || !text.trim()) return [];
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  let i = 0;
  const s = text.replace(/^\uFEFF/, ''); // strip BOM
  while (i < s.length) {
    const c = s[i];
    if (inQuotes) {
      if (c === '"') {
        if (s[i + 1] === '"') { field += '"'; i += 2; continue; }
        inQuotes = false; i += 1; continue;
      }
      field += c; i += 1; continue;
    }
    if (c === '"') { inQuotes = true; i += 1; continue; }
    if (c === ',') { row.push(field); field = ''; i += 1; continue; }
    if (c === '\r') { i += 1; continue; } // swallow CR
    if (c === '\n') {
      row.push(field); rows.push(row); row = []; field = ''; i += 1; continue;
    }
    field += c; i += 1;
  }
  // flush last field/row (files may not end in newline)
  if (field !== '' || row.length) { row.push(field); rows.push(row); }
  // drop trailing all-empty row
  if (rows.length && rows[rows.length - 1].every((v) => v === '')) rows.pop();
  return rows;
}

// PR #620 — case-insensitive column-name matcher.
// Accepts common aliases so users don't have to name the columns exactly
// "inboundMessage" / "idealResponse".
const COLUMN_ALIASES = {
  inboundMessage: ['inboundmessage', 'inbound', 'question', 'q', 'lead', 'leadmessage', 'message', 'user', 'input'],
  idealResponse:  ['idealresponse', 'ideal', 'answer', 'a', 'response', 'reply', 'botreply', 'output'],
  context:        ['context', 'notes', 'note', 'situation'],
  tags:           ['tags', 'tag', 'labels', 'label'],
};
function detectColumns(header) {
  const norm = header.map((h) => (h || '').trim().toLowerCase().replace(/[\s_-]/g, ''));
  const out = {};
  for (const [canonical, aliases] of Object.entries(COLUMN_ALIASES)) {
    const idx = norm.findIndex((h) => aliases.includes(h));
    if (idx !== -1) out[canonical] = idx;
  }
  return out;
}

// ── Helpers (existing) ──────────────────────────────────────────────────────

const TABS = [
  { id: 'scripts',     label: 'Scripts',          icon: FileText   },
  { id: 'examples',    label: 'Examples',          icon: BookOpen   },
  { id: 'personality', label: 'Personality',       icon: Sliders    },
  { id: 'test',        label: 'Test & Feedback',   icon: TestTube2  },
];

function TabBtn({ tab, active, onClick, badge }) {
  const Icon = tab.icon;
  return (
    <button
      onClick={() => onClick(tab.id)}
      className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${
        active
          ? 'bg-civix-purple/20 text-white border border-civix-purple/40'
          : 'text-white/50 hover:text-white hover:bg-civix-panelLight'
      }`}
    >
      <Icon className="w-4 h-4 shrink-0" />
      {tab.label}
      {badge > 0 && (
        <span className="ml-1 px-1.5 py-0.5 rounded-full bg-civix-cyan text-civix-dark text-[10px] font-bold leading-none">
          {badge}
        </span>
      )}
    </button>
  );
}

function StarRating({ value, onChange }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} onClick={() => onChange(n)} className="focus:outline-hidden">
          <Star
            className={`w-5 h-5 transition-colors ${
              n <= value ? 'text-yellow-400 fill-yellow-400' : 'text-white/20'
            }`}
          />
        </button>
      ))}
    </div>
  );
}

function TagInput({ tags, onChange }) {
  const [input, setInput] = useState('');
  function add(e) {
    if ((e.key === 'Enter' || e.key === ',') && input.trim()) {
      e.preventDefault();
      const t = input.trim().replace(/,$/, '');
      if (t && !tags.includes(t)) onChange([...tags, t]);
      setInput('');
    }
  }
  return (
    <div className="flex flex-wrap gap-1 p-2 rounded-lg border border-civix-border/40 bg-civix-panelLight min-h-[38px]">
      {tags.map((t) => (
        <span key={t} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-civix-purple/20 text-civix-purple text-xs">
          {t}
          <button onClick={() => onChange(tags.filter((x) => x !== t))}>
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      <input
        className="flex-1 min-w-[80px] bg-transparent text-sm text-white outline-hidden placeholder-white/30 px-1"
        placeholder="Add tag, press Enter"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={add}
      />
    </div>
  );
}

// ── Scripts tab ──────────────────────────────────────────────────────────────

// PR #621 — ScriptUploadModal
// Upload a PDF, DOCX, TXT, or MD file to a text bot. The file is sent as
// multipart/form-data to POST /:id/scripts/upload; the server extracts text
// and (optionally) splits it into multiple scripts by heading detection.
function ScriptUploadModal({ botId, onClose, onDone }) {
  const [file, setFile]           = useState(null);
  const [title, setTitle]         = useState('');
  const [strategy, setStrategy]   = useState('single');
  const [uploading, setUploading] = useState(false);
  const [result, setResult]       = useState(null);
  const fileInputRef = useRef(null);

  const MAX_MB = 10;
  const ALLOWED_EXT = ['pdf', 'docx', 'txt', 'md', 'csv', 'rtf'];

  function pickFile(f) {
    if (!f) return;
    const ext = (f.name.split('.').pop() || '').toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      toast.error(`Unsupported file type .${ext}. Use ${ALLOWED_EXT.join(', ')}.`);
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`File exceeds ${MAX_MB}MB. Split it into smaller uploads.`);
      return;
    }
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, ''));
  }

  async function handleUpload() {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (title.trim()) fd.append('title', title.trim());
      fd.append('splitStrategy', strategy);
      const res = await apiPost(`/text-bots/${botId}/scripts/upload`, fd);
      setResult(res);
      toast.success(`Added ${res.inserted} script${res.inserted === 1 ? '' : 's'} from ${res.format.toUpperCase()}`);
      onDone();
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="panel w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-civix-cyan" />
            <div className="text-lg font-semibold text-white">Upload Document → Scripts</div>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {!result ? (
          <>
            <div className="text-sm text-white/60">
              Drop a PDF, Word document, or plain-text file. The server extracts the text and stores it as one or
              more scripts on this bot. Great for uploading playbooks, objection handling guides, or rebuttal libraries.
            </div>

            <div
              className="panel-inner p-6 border-2 border-dashed border-white/10 rounded-sm text-center cursor-pointer hover:border-civix-cyan/40 transition-colors"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); }}
              onDrop={(e) => { e.preventDefault(); pickFile(e.dataTransfer.files?.[0]); }}
            >
              <Upload className="w-8 h-8 text-white/40 mx-auto mb-2" />
              {file ? (
                <div>
                  <div className="text-white font-medium">{file.name}</div>
                  <div className="text-white/40 text-xs mt-1">{(file.size / 1024).toFixed(1)} KB — click to replace</div>
                </div>
              ) : (
                <>
                  <div className="text-white/70 text-sm">Drop a file here or click to choose</div>
                  <div className="text-white/40 text-xs mt-1">PDF, DOCX, TXT, MD, CSV — up to {MAX_MB}MB</div>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,.txt,.md,.csv,.rtf,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown,text/csv"
                onChange={(e) => pickFile(e.target.files?.[0])}
                className="hidden"
              />
            </div>

            {file && (
              <>
                <div>
                  <label className="block text-xs text-white/40 mb-1">Script Title (base name)</label>
                  <input
                    className="input w-full"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. Objection Handling Playbook"
                  />
                </div>

                <div>
                  <div className="block text-xs text-white/40 mb-2">Split Strategy</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setStrategy('single')}
                      className={`panel-inner p-3 text-left transition-colors border ${strategy === 'single' ? 'border-civix-cyan bg-civix-cyan/10' : 'border-white/10 hover:border-white/20'}`}
                    >
                      <div className="text-white text-sm font-medium">Single Script</div>
                      <div className="text-white/50 text-xs mt-1">Store the whole document as one script.</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setStrategy('sections')}
                      className={`panel-inner p-3 text-left transition-colors border ${strategy === 'sections' ? 'border-civix-cyan bg-civix-cyan/10' : 'border-white/10 hover:border-white/20'}`}
                    >
                      <div className="text-white text-sm font-medium">Auto-split by Section</div>
                      <div className="text-white/50 text-xs mt-1">Detect headings (# Markdown, ALL CAPS, numbered) and split into one script per section.</div>
                    </button>
                  </div>
                </div>
              </>
            )}

            <div className="flex gap-2 pt-2">
              <button onClick={onClose} className="btn-ghost flex-1">Cancel</button>
              <button
                onClick={handleUpload}
                disabled={uploading || !file}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? 'Uploading…' : 'Upload & Extract'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="panel-inner p-4 space-y-2">
              <div className="flex items-center gap-2 text-civix-cyan">
                <CheckCircle2 className="w-5 h-5" />
                <div className="text-white font-semibold">Upload complete</div>
              </div>
              <div className="text-sm text-white/70">
                Added <span className="text-civix-cyan font-semibold">{result.inserted}</span> script{result.inserted === 1 ? '' : 's'} from a <span className="text-white font-medium">{String(result.format).toUpperCase()}</span> file
                using the <span className="text-white font-medium">{result.strategy === 'sections' ? 'auto-split' : 'single'}</span> strategy.
                Total scripts on bot: <span className="text-white font-semibold">{result.totalScriptsNow}</span>.
              </div>
              {result.scripts?.length > 0 && (
                <div className="text-xs text-white/50 space-y-0.5 max-h-32 overflow-y-auto mt-2">
                  {result.scripts.map((s) => <div key={s._id || s.name}>• {s.name}</div>)}
                </div>
              )}
            </div>
            <button onClick={onClose} className="btn-primary w-full">Done</button>
          </>
        )}
      </div>
    </div>
  );
}

// PR #622 — UrlIngestModal
// Paste a public URL and store the fetched, HTML-stripped text as one or
// more scripts on the bot. Great for marketing pages, blog posts, or
// "Publish to web" Google Docs. Server-side SSRF, size, and content-type
// guards apply.
function UrlIngestModal({ botId, onClose, onDone }) {
  const [url, setUrl]           = useState('');
  const [title, setTitle]       = useState('');
  const [strategy, setStrategy] = useState('single');
  const [saving, setSaving]     = useState(false);
  const [result, setResult]     = useState(null);

  async function handleSubmit() {
    const trimmed = url.trim();
    if (!trimmed) { toast.error('URL required'); return; }
    if (!/^https?:\/\//i.test(trimmed)) { toast.error('URL must start with http:// or https://'); return; }
    setSaving(true);
    try {
      const res = await apiPost(`/text-bots/${botId}/scripts/url`, {
        url:            trimmed,
        title:          title.trim() || undefined,
        splitStrategy:  strategy,
      });
      setResult(res);
      toast.success(`Added ${res.inserted} script${res.inserted === 1 ? '' : 's'} from ${new URL(res.source.url).hostname}`);
      onDone();
    } catch (err) {
      toast.error(err.message || 'URL ingest failed');
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="panel w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-civix-cyan" />
            <div className="text-lg font-semibold text-white">Ingest URL → Scripts</div>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {!result ? (
          <>
            <div className="text-sm text-white/60">
              Paste a public URL. We fetch it, strip HTML, and store the text as one or more scripts.
              Cannot access private or intranet pages.
            </div>
            <div>
              <label className="block text-xs text-white/40 mb-1">URL *</label>
              <input
                className="input w-full"
                placeholder="https://example.com/objection-playbook"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs text-white/40 mb-1">Script Title (optional — defaults to page title)</label>
              <input
                className="input w-full"
                placeholder="Optional override"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div>
              <div className="block text-xs text-white/40 mb-2">Split Strategy</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setStrategy('single')}
                  className={`panel-inner p-3 text-left transition-colors border ${strategy === 'single' ? 'border-civix-cyan bg-civix-cyan/10' : 'border-white/10 hover:border-white/20'}`}
                >
                  <div className="text-white text-sm font-medium">Single Script</div>
                  <div className="text-white/50 text-xs mt-1">Store the whole page as one script.</div>
                </button>
                <button
                  type="button"
                  onClick={() => setStrategy('sections')}
                  className={`panel-inner p-3 text-left transition-colors border ${strategy === 'sections' ? 'border-civix-cyan bg-civix-cyan/10' : 'border-white/10 hover:border-white/20'}`}
                >
                  <div className="text-white text-sm font-medium">Auto-split by Section</div>
                  <div className="text-white/50 text-xs mt-1">Split by headings when detected.</div>
                </button>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={onClose} className="btn-ghost flex-1">Cancel</button>
              <button
                onClick={handleSubmit}
                disabled={saving || !url.trim()}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                {saving ? 'Fetching…' : 'Fetch & Extract'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="panel-inner p-4 space-y-2">
              <div className="flex items-center gap-2 text-civix-cyan">
                <CheckCircle2 className="w-5 h-5" />
                <div className="text-white font-semibold">Ingest complete</div>
              </div>
              <div className="text-sm text-white/70">
                Added <span className="text-civix-cyan font-semibold">{result.inserted}</span> script{result.inserted === 1 ? '' : 's'} from <span className="text-white font-medium">{result.source.title}</span>.
                Total scripts on bot: <span className="text-white font-semibold">{result.totalScriptsNow}</span>.
              </div>
            </div>
            <button onClick={onClose} className="btn-primary w-full">Done</button>
          </>
        )}
      </div>
    </div>
  );
}

// PR #622 — TranscriptIngestModal
// Paste a raw sales/support-call transcript. Backend normalizes it (drops
// [music]/timestamps/boilerplate, cleans speaker labels) and stores as a
// script tagged 'transcript'. Great feed for the AI text agent to model
// real conversational patterns.
function TranscriptIngestModal({ botId, onClose, onDone }) {
  const [text,  setText]  = useState('');
  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const MAX_CHARS = 200_000;

  async function handleSubmit() {
    const t = text.trim();
    if (!t) { toast.error('Transcript text required'); return; }
    if (t.length > MAX_CHARS) { toast.error(`Transcript exceeds ${MAX_CHARS.toLocaleString()} characters`); return; }
    setSaving(true);
    try {
      const res = await apiPost(`/text-bots/${botId}/scripts/transcript`, {
        text:  t,
        title: title.trim() || undefined,
      });
      setResult(res);
      toast.success('Transcript added to training data');
      onDone();
    } catch (err) {
      toast.error(err.message || 'Transcript ingest failed');
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="panel w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-civix-cyan" />
            <div className="text-lg font-semibold text-white">Ingest Sample Transcript</div>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {!result ? (
          <>
            <div className="text-sm text-white/60">
              Paste a real call transcript (SMS, sales call, support call). We clean out timestamps,
              filler markers like <span className="text-white/80">[music]</span> or <span className="text-white/80">[inaudible]</span>, and boilerplate,
              then store it as one script for the AI to learn conversational patterns.
            </div>
            <div>
              <label className="block text-xs text-white/40 mb-1">Title</label>
              <input
                className="input w-full"
                placeholder="e.g. Ryan Discovery Call — Aug 27"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs text-white/40 mb-1">Transcript *</label>
              <textarea
                className="input w-full h-64 font-mono text-xs"
                placeholder={'[00:00] Agent: Hi, is this Ryan?\n[00:03] Prospect: Yeah.\n…'}
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
              <div className="text-xs text-white/40 mt-1">
                {text.length.toLocaleString()} / {MAX_CHARS.toLocaleString()} chars
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={onClose} className="btn-ghost flex-1">Cancel</button>
              <button
                onClick={handleSubmit}
                disabled={saving || !text.trim()}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? 'Saving…' : 'Ingest Transcript'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="panel-inner p-4 space-y-2">
              <div className="flex items-center gap-2 text-civix-cyan">
                <CheckCircle2 className="w-5 h-5" />
                <div className="text-white font-semibold">Transcript added</div>
              </div>
              <div className="text-sm text-white/70">
                Stored <span className="text-white font-medium">{result.script.name}</span> ({result.cleanedChars.toLocaleString()} chars after cleanup).
                Total scripts on bot: <span className="text-white font-semibold">{result.totalScriptsNow}</span>.
              </div>
            </div>
            <button onClick={onClose} className="btn-primary w-full">Done</button>
          </>
        )}
      </div>
    </div>
  );
}

function ScriptsTab({ bot, isOwner, onRefresh }) {
  const [adding, setAdding]   = useState(false);
  const [uploading, setUploading] = useState(false); // PR #621
  const [urlIngest, setUrlIngest]       = useState(false); // PR #622
  const [transcriptIngest, setTranscriptIngest] = useState(false); // PR #622
  const [name, setName]       = useState('');
  const [body, setBody]       = useState('');
  const [tags, setTags]       = useState([]);
  const [saving, setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(null);
  // PR #618: two-click delete confirmation. First click sets confirmId,
  // second click within 4s actually deletes. Prevents accidental single-
  // click destruction of live training scripts (audit finding P2).
  const [confirmId, setConfirmId] = useState(null);
  useEffect(() => {
    if (!confirmId) return;
    const t = setTimeout(() => setConfirmId(null), 4000);
    return () => clearTimeout(t);
  }, [confirmId]);

  async function handleAdd(e) {
    e.preventDefault();
    if (!name.trim() || !body.trim()) return;
    setSaving(true);
    try {
      await apiPost(`/text-bots/${bot._id}/scripts`, { name: name.trim(), body: body.trim(), tags });
      toast.success('Script added');
      setAdding(false); setName(''); setBody(''); setTags([]);
      onRefresh();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  }

  async function handleDelete(scriptId) {
    if (confirmId !== scriptId) { setConfirmId(scriptId); return; }
    setConfirmId(null);
    setDeleting(scriptId);
    try {
      await apiDel(`/text-bots/${bot._id}/scripts/${scriptId}`);
      toast.success('Script removed');
      onRefresh();
    } catch (err) { toast.error(err.message); }
    finally { setDeleting(null); }
  }

  const scripts = bot.scripts || [];

  return (
    <div className="space-y-4">
      {isOwner && (
        <div className="flex justify-end gap-2 flex-wrap">
          <button
            onClick={() => setTranscriptIngest(true)}
            className="btn-ghost flex items-center gap-2"
            title="Paste a call transcript to train the AI"
          >
            <MessageSquare className="w-4 h-4" /> Add Transcript
          </button>
          <button
            onClick={() => setUrlIngest(true)}
            className="btn-ghost flex items-center gap-2"
            title="Fetch a public URL and store as a script"
          >
            <Zap className="w-4 h-4" /> Add URL
          </button>
          <button
            onClick={() => setUploading(true)}
            className="btn-ghost flex items-center gap-2"
            title="Upload a PDF, Word document, or text file"
          >
            <Upload className="w-4 h-4" /> Upload File
          </button>
          <button onClick={() => setAdding(!adding)} className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add Script
          </button>
        </div>
      )}

      {uploading && (
        <ScriptUploadModal
          botId={bot._id}
          onClose={() => setUploading(false)}
          onDone={() => { setUploading(false); onRefresh(); }}
        />
      )}

      {urlIngest && (
        <UrlIngestModal
          botId={bot._id}
          onClose={() => setUrlIngest(false)}
          onDone={() => { setUrlIngest(false); onRefresh(); }}
        />
      )}

      {transcriptIngest && (
        <TranscriptIngestModal
          botId={bot._id}
          onClose={() => setTranscriptIngest(false)}
          onDone={() => { setTranscriptIngest(false); onRefresh(); }}
        />
      )}

      {adding && (
        <form onSubmit={handleAdd} className="panel p-4 space-y-3">
          <div className="text-sm font-medium text-white/80">New Script</div>
          <div>
            <label className="block text-xs text-white/40 mb-1">Script Name *</label>
            <input className="input w-full" placeholder="e.g. Greeting, Follow-Up, Objection – Price" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </div>
          <div>
            <label className="block text-xs text-white/40 mb-1">Script Body *</label>
            <textarea className="input w-full h-28 resize-none" placeholder="Hi {firstName}! This is {businessName}. Are you still interested in..." value={body} onChange={(e) => setBody(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs text-white/40 mb-1">Tags</label>
            <TagInput tags={tags} onChange={setTags} />
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setAdding(false)} className="btn-ghost flex-1">Cancel</button>
            <button type="submit" disabled={saving || !name.trim() || !body.trim()} className="btn-primary flex-1">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </button>
          </div>
        </form>
      )}

      {scripts.length === 0 && !adding ? (
        <div className="panel p-8 text-center text-white/40 text-sm">
          No scripts yet. Add greeting, follow-up, objection, booking templates…
        </div>
      ) : (
        <div className="space-y-3">
          {scripts.map((s) => (
            <div key={s._id} className="panel p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-white text-sm mb-1">{s.name}</div>
                  <div className="text-white/60 text-sm whitespace-pre-wrap">{s.body}</div>
                  {s.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {s.tags.map((t) => (
                        <span key={t} className="px-2 py-0.5 rounded-full bg-civix-purple/10 text-civix-purple text-xs">{t}</span>
                      ))}
                    </div>
                  )}
                </div>
                {isOwner && (
                  <button
                    onClick={() => handleDelete(s._id)}
                    disabled={deleting === s._id}
                    title={confirmId === s._id ? 'Click again to confirm delete' : 'Delete script'}
                    className={`shrink-0 transition-colors ${confirmId === s._id ? 'text-red-400 font-medium text-xs px-2 py-1 rounded-sm bg-red-400/10 border border-red-400/30' : 'text-white/30 hover:text-red-400'}`}
                  >
                    {deleting === s._id ? <Loader2 className="w-4 h-4 animate-spin" /> : confirmId === s._id ? 'Confirm delete?' : <Trash2 className="w-4 h-4" />}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Examples tab ─────────────────────────────────────────────────────────────

// PR #620 — BulkImportModal
// Lets an agent bulk-upload Q&A pairs (training examples) from a CSV.
// The CSV is parsed 100% in the browser — the backend receives already-
// normalized JSON rows. Supports both file upload and paste-in flows.
// Accepts common column-name aliases (see COLUMN_ALIASES).
function BulkImportModal({ botId, onClose, onDone }) {
  const [rawText, setRawText]   = useState('');
  const [parsed, setParsed]     = useState(null); // { header, rows, colMap, badCount }
  const [uploading, setUploading] = useState(false);
  const [result, setResult]     = useState(null);
  const fileInputRef = useRef(null);

  function handleFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) {
      toast.error('File exceeds 2MB. Split into smaller batches.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '');
      setRawText(text);
      parseAndPreview(text);
    };
    reader.onerror = () => toast.error('Could not read file');
    reader.readAsText(f);
  }

  function parseAndPreview(text) {
    const rows = parseCSV(text);
    if (!rows.length) { setParsed(null); toast.error('Empty CSV'); return; }
    if (rows.length < 2) { setParsed(null); toast.error('CSV needs a header row + at least one data row'); return; }
    const header = rows[0];
    const dataRows = rows.slice(1);
    const colMap = detectColumns(header);
    if (colMap.inboundMessage === undefined || colMap.idealResponse === undefined) {
      toast.error('CSV must include an inbound-message column and an ideal-response column (see aliases below the preview)');
      setParsed({ header, rows: dataRows, colMap, badCount: dataRows.length });
      return;
    }
    let badCount = 0;
    for (const r of dataRows) {
      const im = (r[colMap.inboundMessage] || '').trim();
      const ir = (r[colMap.idealResponse]  || '').trim();
      if (!im || !ir) badCount += 1;
    }
    setParsed({ header, rows: dataRows, colMap, badCount });
  }

  async function handleSubmit() {
    if (!parsed || parsed.colMap.inboundMessage === undefined || parsed.colMap.idealResponse === undefined) return;
    const { rows, colMap } = parsed;
    // Slice to 500 max (server also enforces; slice on client so we don't 400).
    const clipped = rows.slice(0, 500);
    const payload = clipped.map((r) => ({
      inboundMessage: (r[colMap.inboundMessage] || '').trim(),
      idealResponse:  (r[colMap.idealResponse]  || '').trim(),
      context:        colMap.context !== undefined ? (r[colMap.context] || '').trim() : '',
      tags:           colMap.tags    !== undefined
        ? (r[colMap.tags] || '').split(/[|;,]/).map((t) => t.trim()).filter(Boolean)
        : [],
    }));
    setUploading(true);
    try {
      const res = await apiPost(`/text-bots/${botId}/examples/bulk`, { rows: payload });
      setResult(res);
      if (res.inserted > 0) toast.success(`Imported ${res.inserted} example${res.inserted === 1 ? '' : 's'}`);
      if (res.skipped > 0)  toast(`${res.skipped} row${res.skipped === 1 ? '' : 's'} skipped (blank fields)`, { icon: '⚠️' });
      // Refresh in the background so the Examples list reflects the imports.
      onDone();
    } catch (err) {
      toast.error(err.message || 'Import failed');
    } finally {
      setUploading(false);
    }
  }

  function downloadTemplate() {
    const csv = [
      'inboundMessage,idealResponse,context,tags',
      '"How much does it cost?","Plans start at $20/mo. Want a free quote?","warm lead from Facebook ad","price;quote"',
      '"Are you a scam?","Totally understand the concern! We’re a licensed agency with A+ BBB rating.","scam objection","objection;trust"',
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'textbot-examples-template.csv';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }

  const validCount = parsed ? (parsed.rows.length - parsed.badCount) : 0;
  const overCap = parsed ? parsed.rows.length > 500 : false;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="panel w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-civix-cyan" />
            <div className="text-lg font-semibold text-white">Bulk Import Q&A Examples</div>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {!result ? (
          <>
            <div className="text-sm text-white/60">
              Upload a CSV with columns for the inbound message and the ideal reply. Each row becomes a training
              example (with a semantic embedding). Up to 500 rows per upload.
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button onClick={() => fileInputRef.current?.click()} className="btn-ghost flex items-center gap-2">
                <Upload className="w-4 h-4" /> Choose CSV file
              </button>
              <input ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={handleFile} className="hidden" />
              <button onClick={downloadTemplate} className="btn-ghost flex items-center gap-2">
                <Download className="w-4 h-4" /> Download template
              </button>
            </div>

            <div>
              <label className="block text-xs text-white/40 mb-1">… or paste CSV text</label>
              <textarea
                className="input w-full h-32 resize-none font-mono text-xs"
                placeholder="inboundMessage,idealResponse,context,tags&#10;How much does it cost?,Plans start at $20/mo...,warm lead,price;quote"
                value={rawText}
                onChange={(e) => { setRawText(e.target.value); parseAndPreview(e.target.value); }}
              />
            </div>

            {parsed && (
              <div className="space-y-2">
                <div className="text-sm text-white/80">
                  Detected {parsed.rows.length} row{parsed.rows.length === 1 ? '' : 's'}
                  {' — '}
                  <span className="text-civix-cyan">{validCount} valid</span>
                  {parsed.badCount > 0 && <span className="text-yellow-400">, {parsed.badCount} skipped (blank fields)</span>}
                  {overCap && <span className="text-orange-400">, capped to first 500</span>}
                </div>

                <div className="text-xs text-white/50">
                  Column mapping:
                  <span className="ml-2">
                    inbound = <code className="text-white/80">{parsed.header[parsed.colMap.inboundMessage] ?? '— not found —'}</code>
                  </span>
                  <span className="ml-3">
                    ideal = <code className="text-white/80">{parsed.header[parsed.colMap.idealResponse] ?? '— not found —'}</code>
                  </span>
                  {parsed.colMap.context !== undefined && (
                    <span className="ml-3">context = <code className="text-white/80">{parsed.header[parsed.colMap.context]}</code></span>
                  )}
                  {parsed.colMap.tags !== undefined && (
                    <span className="ml-3">tags = <code className="text-white/80">{parsed.header[parsed.colMap.tags]}</code></span>
                  )}
                </div>

                {validCount > 0 && (
                  <div className="panel-inner p-2 text-xs text-white/70 max-h-40 overflow-y-auto">
                    <div className="font-semibold text-white/80 mb-1">Preview (first 3 valid rows):</div>
                    {parsed.rows.slice(0, 3).map((r, i) => {
                      const im = (r[parsed.colMap.inboundMessage] || '').trim();
                      const ir = (r[parsed.colMap.idealResponse] || '').trim();
                      if (!im || !ir) return null;
                      return (
                        <div key={i} className="py-1">
                          <div className="text-white/60">Lead: {im.slice(0, 80)}{im.length > 80 ? '…' : ''}</div>
                          <div className="text-civix-cyan">Bot:  {ir.slice(0, 80)}{ir.length > 80 ? '…' : ''}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <div className="text-xs text-white/40">
              Recognized column names (case-insensitive): <br/>
              &nbsp;• inbound: inboundMessage, inbound, question, q, lead, leadmessage, message, user, input <br/>
              &nbsp;• ideal:   idealResponse, ideal, answer, a, response, reply, botreply, output <br/>
              &nbsp;• context (optional): context, notes, note, situation <br/>
              &nbsp;• tags (optional): tags, tag, labels, label (split on <code>|</code>, <code>;</code>, or <code>,</code>)
            </div>

            <div className="flex gap-2 pt-2">
              <button onClick={onClose} className="btn-ghost flex-1">Cancel</button>
              <button
                onClick={handleSubmit}
                disabled={uploading || !parsed || validCount === 0}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? 'Importing…' : `Import ${Math.min(validCount, 500)} example${validCount === 1 ? '' : 's'}`}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="panel-inner p-4 space-y-2">
              <div className="flex items-center gap-2 text-civix-cyan">
                <CheckCircle2 className="w-5 h-5" />
                <div className="text-white font-semibold">Import complete</div>
              </div>
              <div className="text-sm text-white/70">
                Inserted <span className="text-civix-cyan font-semibold">{result.inserted}</span> example{result.inserted === 1 ? '' : 's'}.
                {result.skipped > 0 && <> Skipped <span className="text-yellow-400">{result.skipped}</span> row{result.skipped === 1 ? '' : 's'} with missing fields.</>}
                {' '}Total examples on bot: <span className="text-white font-semibold">{result.totalExamplesNow}</span>.
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={onClose} className="btn-primary w-full">Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ExamplesTab({ bot, isOwner, onRefresh }) {
  const [adding, setAdding]   = useState(false);
  const [inbound, setInbound] = useState('');
  const [ideal, setIdeal]     = useState('');
  const [context, setContext] = useState('');
  const [tags, setTags]       = useState([]);
  const [saving, setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [importing, setImporting] = useState(false); // PR #620
  // PR #618: two-click delete confirmation (see ScriptsTab for rationale).
  const [confirmId, setConfirmId] = useState(null);
  useEffect(() => {
    if (!confirmId) return;
    const t = setTimeout(() => setConfirmId(null), 4000);
    return () => clearTimeout(t);
  }, [confirmId]);

  async function handleAdd(e) {
    e.preventDefault();
    if (!inbound.trim() || !ideal.trim()) return;
    setSaving(true);
    try {
      await apiPost(`/text-bots/${bot._id}/examples`, {
        inboundMessage: inbound.trim(), idealResponse: ideal.trim(), context: context.trim(), tags,
      });
      toast.success('Example added (embedding stored for semantic search)');
      setAdding(false); setInbound(''); setIdeal(''); setContext(''); setTags([]);
      onRefresh();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  }

  async function handleDelete(exId) {
    if (confirmId !== exId) { setConfirmId(exId); return; }
    setConfirmId(null);
    setDeleting(exId);
    try {
      await apiDel(`/text-bots/${bot._id}/examples/${exId}`);
      toast.success('Example removed');
      onRefresh();
    } catch (err) { toast.error(err.message); }
    finally { setDeleting(null); }
  }

  const examples = bot.responseExamples || [];

  return (
    <div className="space-y-4">
      {isOwner && (
        <div className="flex justify-end">
          <button onClick={() => setAdding(!adding)} className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add Example
          </button>
        </div>
      )}

      {adding && (
        <form onSubmit={handleAdd} className="panel p-4 space-y-3">
          <div className="text-sm font-medium text-white/80">New Q&A Example</div>
          <div>
            <label className="block text-xs text-white/40 mb-1">Inbound Message *</label>
            <input className="input w-full" placeholder="e.g. How much does it cost?" value={inbound} onChange={(e) => setInbound(e.target.value)} autoFocus />
          </div>
          <div>
            <label className="block text-xs text-white/40 mb-1">Ideal Response *</label>
            <textarea className="input w-full h-20 resize-none" placeholder="Great question! Plans start at $20/mo. Want a free quote? Reply QUOTE to opt out." value={ideal} onChange={(e) => setIdeal(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs text-white/40 mb-1">Context (optional)</label>
            <input className="input w-full" placeholder="e.g. warm lead asking about price on Facebook ad" value={context} onChange={(e) => setContext(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs text-white/40 mb-1">Tags</label>
            <TagInput tags={tags} onChange={setTags} />
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setAdding(false)} className="btn-ghost flex-1">Cancel</button>
            <button type="submit" disabled={saving || !inbound.trim() || !ideal.trim()} className="btn-primary flex-1">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </button>
          </div>
        </form>
      )}

      {examples.length === 0 && !adding ? (
        <div className="panel p-8 text-center text-white/40 text-sm">
          No examples yet. Add Q&A pairs — the bot uses these as training signal for responses.
        </div>
      ) : (
        <div className="space-y-3">
          {examples.map((ex) => (
            <div key={ex._id} className="panel p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0 space-y-2">
                  <div>
                    <div className="text-[10px] text-white/40 uppercase tracking-wider mb-0.5">Inbound</div>
                    <div className="text-white/80 text-sm">{ex.inboundMessage}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-civix-cyan/60 uppercase tracking-wider mb-0.5">Ideal Response</div>
                    <div className="text-civix-cyan text-sm">{ex.idealResponse}</div>
                  </div>
                  {ex.context && (
                    <div className="text-xs text-white/40 italic">{ex.context}</div>
                  )}
                  {ex.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {ex.tags.map((t) => (
                        <span key={t} className="px-2 py-0.5 rounded-full bg-civix-purple/10 text-civix-purple text-xs">{t}</span>
                      ))}
                    </div>
                  )}
                </div>
                {isOwner && (
                  <button
                    onClick={() => handleDelete(ex._id)}
                    disabled={deleting === ex._id}
                    title={confirmId === ex._id ? 'Click again to confirm delete' : 'Delete example'}
                    className={`shrink-0 transition-colors ${confirmId === ex._id ? 'text-red-400 font-medium text-xs px-2 py-1 rounded-sm bg-red-400/10 border border-red-400/30' : 'text-white/30 hover:text-red-400'}`}
                  >
                    {deleting === ex._id ? <Loader2 className="w-4 h-4 animate-spin" /> : confirmId === ex._id ? 'Confirm delete?' : <Trash2 className="w-4 h-4" />}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Personality tab ───────────────────────────────────────────────────────────

function PersonalityTab({ bot, isOwner, onRefresh }) {
  // PR #618: derive form seed from bot; a useEffect below re-syncs local
  // form state whenever the parent-refetched `bot` changes. Fixes the
  // audit-flagged badge/toggle desync where the header showed
  // `bot.autoReply` (fresh) while the toggle rendered stale local `form`.
  const seed = (b) => ({
    tone:          b.personality?.tone      || 'friendly',
    formality:     b.personality?.formality || 'semi-formal',
    useEmojis:     b.personality?.useEmojis ?? false,
    maxLength:     b.personality?.maxLength ?? 160,
    includeOptOut: b.compliance?.includeOptOut ?? true,
    optOutText:    b.compliance?.optOutText    || 'Reply STOP to opt out.',
    businessName:  b.compliance?.businessName  || '',
    autoReply:     b.autoReply ?? false,
    // PR #624 — per-bot booking config (surface only; engine wiring pending).
    slotMinutes:          b.bookingConfig?.slotMinutes          ?? 0,
    bufferMinutes:        b.bookingConfig?.bufferMinutes        ?? 0,
    bookingWindowDays:    b.bookingConfig?.bookingWindowDays    ?? 14,
    confirmationTemplate: b.bookingConfig?.confirmationTemplate ?? '',
    rescheduleEnabled:    b.bookingConfig?.rescheduleEnabled    ?? true,
    cancelEnabled:        b.bookingConfig?.cancelEnabled        ?? true,
  });
  const [form, setForm]   = useState(() => seed(bot));
  useEffect(() => {
    // Re-seed when the bot object identity changes OR any field the form
    // mirrors changes. Guards against user-in-flight edits by only re-
    // seeding while the form is not currently saving.
    setForm(seed(bot));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bot._id,
    bot.autoReply,
    bot.personality?.tone,
    bot.personality?.formality,
    bot.personality?.useEmojis,
    bot.personality?.maxLength,
    bot.compliance?.includeOptOut,
    bot.compliance?.optOutText,
    bot.compliance?.businessName,
    bot.bookingConfig?.slotMinutes,
    bot.bookingConfig?.bufferMinutes,
    bot.bookingConfig?.bookingWindowDays,
    bot.bookingConfig?.confirmationTemplate,
    bot.bookingConfig?.rescheduleEnabled,
    bot.bookingConfig?.cancelEnabled,
  ]);
  const [saving, setSaving] = useState(false);

  function set(key, val) { setForm((prev) => ({ ...prev, [key]: val })); }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await apiPut(`/text-bots/${bot._id}`, {
        autoReply: form.autoReply,
        personality: {
          tone:      form.tone,
          formality: form.formality,
          useEmojis: form.useEmojis,
          maxLength: Number(form.maxLength) || 160,
        },
        compliance: {
          includeOptOut: form.includeOptOut,
          optOutText:    form.optOutText,
          businessName:  form.businessName,
        },
        bookingConfig: {
          slotMinutes:          Number(form.slotMinutes)       || 0,
          bufferMinutes:        Number(form.bufferMinutes)     || 0,
          bookingWindowDays:    Number(form.bookingWindowDays) || 14,
          confirmationTemplate: form.confirmationTemplate || '',
          rescheduleEnabled:    Boolean(form.rescheduleEnabled),
          cancelEnabled:        Boolean(form.cancelEnabled),
        },
      });
      toast.success('Settings saved');
      onRefresh();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  }

  return (
    <form onSubmit={handleSave} className="max-w-lg space-y-6">
      {/* Personality */}
      <div className="panel p-5 space-y-4">
        <div className="text-sm font-medium text-white/80">Personality</div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-white/40 mb-1">Tone</label>
            <select className="input w-full" value={form.tone} onChange={(e) => set('tone', e.target.value)} disabled={!isOwner}>
              <option value="friendly">Friendly</option>
              <option value="professional">Professional</option>
              <option value="casual">Casual</option>
              <option value="warm">Warm</option>
              <option value="direct">Direct</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-white/40 mb-1">Formality</label>
            <select className="input w-full" value={form.formality} onChange={(e) => set('formality', e.target.value)} disabled={!isOwner}>
              <option value="formal">Formal</option>
              <option value="semi-formal">Semi-Formal</option>
              <option value="casual">Casual</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-white/40 mb-1">Max Length (chars)</label>
            <input type="number" className="input w-full" min={50} max={1600} value={form.maxLength} onChange={(e) => set('maxLength', e.target.value)} disabled={!isOwner} />
            <div className="text-[10px] text-white/30 mt-1">160 = 1 SMS segment</div>
          </div>
          <div className="flex flex-col justify-center gap-2">
            <div className="flex items-center gap-3">
              <span className="text-xs text-white/50">Use Emojis</span>
              <button
                type="button"
                disabled={!isOwner}
                onClick={() => set('useEmojis', !form.useEmojis)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${form.useEmojis ? 'bg-civix-cyan' : 'bg-civix-border'} ${!isOwner ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${form.useEmojis ? 'translate-x-4.5' : 'translate-x-0.5'}`} />
              </button>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-white/50">Auto-Reply</span>
              <button
                type="button"
                disabled={!isOwner}
                onClick={() => set('autoReply', !form.autoReply)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${form.autoReply ? 'bg-civix-cyan' : 'bg-civix-border'} ${!isOwner ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${form.autoReply ? 'translate-x-4.5' : 'translate-x-0.5'}`} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Compliance — TCPA */}
      <div className="panel p-5 space-y-4">
        <div className="flex items-center gap-2">
          <div className="text-sm font-medium text-white/80">Compliance (TCPA)</div>
          <span className="px-1.5 py-0.5 rounded-sm bg-yellow-400/10 text-yellow-400 text-[10px] font-semibold">Required by law</span>
        </div>

        <div>
          <label className="block text-xs text-white/40 mb-1">Business Name</label>
          <input className="input w-full" placeholder="Your business name" value={form.businessName} onChange={(e) => set('businessName', e.target.value)} disabled={!isOwner} />
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            disabled={!isOwner}
            onClick={() => set('includeOptOut', !form.includeOptOut)}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${form.includeOptOut ? 'bg-civix-cyan' : 'bg-civix-border'} ${!isOwner ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${form.includeOptOut ? 'translate-x-4.5' : 'translate-x-0.5'}`} />
          </button>
          <span className="text-xs text-white/50">Include opt-out in every message</span>
        </div>

        <div>
          <label className="block text-xs text-white/40 mb-1">Opt-Out Text</label>
          <input className="input w-full" placeholder="Reply STOP to opt out." value={form.optOutText} onChange={(e) => set('optOutText', e.target.value)} disabled={!isOwner} />
        </div>
      </div>

      {/* Booking config — PR #624 (config storage + UI; engine wiring pending) */}
      <div className="panel p-5 space-y-4">
        <div className="flex items-center gap-2">
          <div className="text-sm font-medium text-white/80">Booking</div>
          <span className="px-1.5 py-0.5 rounded-sm bg-white/5 text-white/40 text-[10px]">Per-bot</span>
        </div>
        <div className="text-[11px] text-white/40 -mt-2">
          These settings apply to the AI Schedule bot's appointment flow. Leave a field at 0 to use the global default.
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-xs text-white/40 mb-1">Appointment length (min)</label>
            <input type="number" className="input w-full" min={0} max={240} step={5}
                   value={form.slotMinutes}
                   onChange={(e) => set('slotMinutes', e.target.value)} disabled={!isOwner} />
            <div className="text-[10px] text-white/30 mt-1">0 = use global default</div>
          </div>
          <div>
            <label className="block text-xs text-white/40 mb-1">Buffer around bookings (min)</label>
            <input type="number" className="input w-full" min={0} max={120} step={5}
                   value={form.bufferMinutes}
                   onChange={(e) => set('bufferMinutes', e.target.value)} disabled={!isOwner} />
          </div>
          <div>
            <label className="block text-xs text-white/40 mb-1">Offer window (days ahead)</label>
            <input type="number" className="input w-full" min={1} max={60} step={1}
                   value={form.bookingWindowDays}
                   onChange={(e) => set('bookingWindowDays', e.target.value)} disabled={!isOwner} />
          </div>
        </div>

        <div>
          <label className="block text-xs text-white/40 mb-1">Confirmation template (optional)</label>
          <textarea
            className="input w-full h-20 resize-none"
            placeholder="e.g. Hi {lead.firstName}, you're confirmed for {slot.label}. Reply RESCHEDULE to change."
            value={form.confirmationTemplate}
            onChange={(e) => set('confirmationTemplate', e.target.value)}
            disabled={!isOwner}
            maxLength={1600}
          />
          <div className="text-[10px] text-white/30 mt-1">
            Tokens: {'{lead.firstName}'}, {'{slot.label}'}, {'{agent.firstName}'}. Leave blank to use the built-in copy.
          </div>
        </div>

        <div className="flex items-center gap-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled={!isOwner}
              onClick={() => set('rescheduleEnabled', !form.rescheduleEnabled)}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${form.rescheduleEnabled ? 'bg-civix-cyan' : 'bg-civix-border'} ${!isOwner ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${form.rescheduleEnabled ? 'translate-x-4.5' : 'translate-x-0.5'}`} />
            </button>
            <span className="text-xs text-white/50">Allow reschedule via SMS</span>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled={!isOwner}
              onClick={() => set('cancelEnabled', !form.cancelEnabled)}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${form.cancelEnabled ? 'bg-civix-cyan' : 'bg-civix-border'} ${!isOwner ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${form.cancelEnabled ? 'translate-x-4.5' : 'translate-x-0.5'}`} />
            </button>
            <span className="text-xs text-white/50">Allow cancel via SMS</span>
          </div>
        </div>
      </div>

      {isOwner && (
        <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Settings
        </button>
      )}
    </form>
  );
}

// ── Test & Feedback tab ───────────────────────────────────────────────────────

// Suggested product-interest values for the simulate-context panel's
// datalist. Mirrors the kinds of values seen in real Lead.productInterest /
// customFields.productInterest data, so testers can pick a realistic one
// instead of guessing at exact wording.
const SIMULATE_PRODUCT_INTEREST_OPTIONS = [
  'mortgage protection',
  'final expense coverage',
  'life insurance',
  'an IUL / cash-value life policy',
  'veterans life insurance',
];

function TestTab({ bot, isOwner, onRefresh }) {
  const [inbound, setInbound]     = useState('');
  const [response, setResponse]   = useState(null);
  const [reasoning, setReasoning] = useState('');
  const [simulated, setSimulated] = useState(false);
  const [generating, setGenerating] = useState(false);

  // PR #417 — running multi-turn conversation. Empty ⇒ next Generate is the
  // opener (deterministic bypass); non-empty ⇒ follow-up (LLM path with this
  // history injected into the prompt). `lastBypass` records which path the API
  // actually took on the most recent Generate ('opener' vs undefined).
  const [conversationHistory, setConversationHistory] = useState([]);
  const [lastBypass, setLastBypass] = useState(null);
  const [lastInbound, setLastInbound] = useState('');

  // "Simulate lead context" panel — lets a tester preview how {{tokens}}
  // in scripts/examples render for a hypothetical lead/agent, without
  // needing a real Lead record. Empty fields are omitted from the request
  // so the backend's normal fallback rules (real lead data, then silent
  // defaults) still apply.
  const [simulateOpen, setSimulateOpen] = useState(false);
  const [simFirstName, setSimFirstName] = useState('');
  const [simAgentFirstName, setSimAgentFirstName] = useState('');
  const [simProductInterest, setSimProductInterest] = useState('');
  const [simLeadSource, setSimLeadSource] = useState('');
  const [simCity, setSimCity] = useState('');
  const [simState, setSimState] = useState('');

  const [rating, setRating]         = useState(0);
  const [correction, setCorrection] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedbackDone, setFeedbackDone] = useState(false);

  const [analyzing, setAnalyzing]   = useState(false);
  const [proposed, setProposed]     = useState(null);
  const [applying, setApplying]     = useState(false);

  const qc = useQueryClient();

  // Build the simulateContext payload from the panel's fields, omitting
  // anything left blank so the backend can fall back to real lead/agent
  // data (or its own silent defaults) for those fields.
  function buildSimulateContext() {
    const ctx = {};
    if (simFirstName.trim())       ctx.firstName = simFirstName.trim();
    if (simAgentFirstName.trim())  ctx.agentFirstName = simAgentFirstName.trim();
    if (simProductInterest.trim()) ctx.productInterest = simProductInterest.trim();
    if (simLeadSource.trim())      ctx.leadSource = simLeadSource.trim();
    if (simCity.trim())            ctx.city = simCity.trim();
    if (simState.trim())           ctx.state = simState.trim();
    return Object.keys(ctx).length ? ctx : undefined;
  }

  async function handleGenerate(e) {
    e.preventDefault();
    const text = inbound.trim();
    if (!text) return;
    setGenerating(true);
    setResponse(null); setReasoning('');
    setRating(0); setCorrection(''); setFeedbackDone(false); setProposed(null);
    try {
      const simulateContext = buildSimulateContext();
      const res = await apiPost(`/text-bots/${bot._id}/test`, {
        inboundMessage: text,
        conversationHistory,
        ...(simulateContext ? { simulateContext } : {}),
      });
      const botText = res.response || '';
      setResponse(botText);
      setReasoning(res.reasoning || '');
      setSimulated(res.simulated || false);
      setLastBypass(res.bypass || null);
      setLastInbound(text);
      // Append this exchange so the NEXT Generate is treated as a follow-up.
      setConversationHistory((h) => [
        ...h,
        { role: 'lead', text },
        { role: 'bot', text: botText },
      ]);
      setInbound('');
    } catch (err) { toast.error(err.message); }
    finally { setGenerating(false); }
  }

  function handleResetConversation() {
    setConversationHistory([]);
    setResponse(null); setReasoning(''); setSimulated(false); setLastBypass(null);
    setInbound(''); setLastInbound('');
    setRating(0); setCorrection(''); setFeedbackDone(false);
  }

  // Turn indicator — Turn 1 is the opener (empty history); each completed
  // lead⇄bot exchange advances the turn. `lastBypass` reveals which path the
  // last Generate actually fired.
  const turnNumber = conversationHistory.length / 2 + 1;
  const isOpenerTurn = conversationHistory.length === 0;

  async function handleFeedback(e) {
    e.preventDefault();
    if (!rating) { toast.error('Please rate the response'); return; }
    setSubmitting(true);
    try {
      await apiPost(`/text-bots/${bot._id}/feedback`, {
        testInbound: lastInbound,
        generatedResponse: response,
        rating,
        correction: correction.trim(),
      });
      toast.success('Feedback saved');
      setFeedbackDone(true);
      onRefresh();
    } catch (err) { toast.error(err.message); }
    finally { setSubmitting(false); }
  }

  // PR #623 — one-click training loop.
  // Thumbs UP writes a training example { lastInbound → response } so the
  // bot immediately learns that answer. Thumbs DOWN (with an optional
  // correction) writes { lastInbound → correction } when a correction is
  // provided; otherwise just records negative feedback. Server dedupes so
  // double-clicks don't spam duplicate examples.
  async function handleQuickFeedback(verdict) {
    if (submitting) return;
    if (!response || !lastInbound) return;
    setSubmitting(true);
    try {
      const payload = {
        testInbound: lastInbound,
        generatedResponse: response,
        verdict,
      };
      if (verdict === 'down' && correction.trim()) {
        payload.correction = correction.trim();
      }
      const res = await apiPost(`/text-bots/${bot._id}/feedback/quick`, payload);
      if (res.example) {
        toast.success(verdict === 'up'
          ? 'Saved as training example — the bot will use this'
          : 'Correction saved as training example');
      } else {
        toast.success(verdict === 'down'
          ? 'Marked bad. Add a correction for the bot to learn from.'
          : 'Feedback recorded');
      }
      setFeedbackDone(true);
      onRefresh();
    } catch (err) { toast.error(err.message); }
    finally { setSubmitting(false); }
  }

  async function handleAnalyze() {
    setAnalyzing(true); setProposed(null);
    try {
      const res = await apiGet(`/text-bots/${bot._id}/feedback-summary`);
      if (!res.hasEnoughData) { toast('Not enough feedback to analyze yet'); return; }
      setProposed(res.proposed);
    } catch (err) { toast.error(err.message); }
    finally { setAnalyzing(false); }
  }

  async function handleApply() {
    if (!proposed) return;
    setApplying(true);
    try {
      await apiPost(`/text-bots/${bot._id}/apply-feedback`, {
        personality: proposed.personality,
        compliance:  proposed.compliance,
      });
      toast.success('Personality updated from feedback');
      setProposed(null);
      onRefresh();
      qc.invalidateQueries({ queryKey: ['text-bot', bot._id] });
    } catch (err) { toast.error(err.message); }
    finally { setApplying(false); }
  }

  const recentFeedback = (bot.feedback || []).slice(-10).reverse();

  return (
    <div className="space-y-6">
      {/* Chat panel */}
      <div className="panel p-5 space-y-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-white/80">Test Your Bot</span>
            <span className={`text-[11px] px-2 py-0.5 rounded-full border ${isOpenerTurn ? 'bg-civix-purple/15 text-civix-purple border-civix-purple/30' : 'bg-civix-cyan/15 text-civix-cyan border-civix-cyan/30'}`}>
              Turn {turnNumber} {isOpenerTurn ? '(opener)' : '(follow-up)'}
              {lastBypass === 'opener' ? ' · bypass' : lastBypass === null && !isOpenerTurn ? ' · LLM' : ''}
            </span>
          </div>
          {conversationHistory.length > 0 && (
            <button
              type="button"
              onClick={handleResetConversation}
              className="text-[11px] text-white/40 hover:text-white/70 transition-colors flex items-center gap-1"
            >
              <RotateCcw className="w-3 h-3" /> Reset conversation
            </button>
          )}
        </div>

        {/* Simulate lead context — preview {{token}} rendering without a real lead */}
        <div className="rounded-lg border border-white/10 bg-white/2">
          <button
            type="button"
            onClick={() => setSimulateOpen((v) => !v)}
            className="w-full flex items-center justify-between px-3 py-2.5 text-xs font-medium text-white/60 hover:text-white/80 transition-colors"
          >
            <span>Simulate lead context</span>
            <ChevronRight className={`w-3.5 h-3.5 transition-transform ${simulateOpen ? 'rotate-90' : ''}`} />
          </button>
          {simulateOpen && (
            <div className="px-3 pb-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
              <div>
                <label className="block text-[11px] text-white/40 mb-1">First name</label>
                <input
                  className="input text-sm w-full"
                  placeholder="Tom"
                  value={simFirstName}
                  onChange={(e) => setSimFirstName(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-[11px] text-white/40 mb-1">Agent first name</label>
                <input
                  className="input text-sm w-full"
                  placeholder="Easton"
                  value={simAgentFirstName}
                  onChange={(e) => setSimAgentFirstName(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-[11px] text-white/40 mb-1">Product interest</label>
                <input
                  className="input text-sm w-full"
                  placeholder="mortgage protection"
                  list="simulate-product-interest-options"
                  value={simProductInterest}
                  onChange={(e) => setSimProductInterest(e.target.value)}
                />
                <datalist id="simulate-product-interest-options">
                  {SIMULATE_PRODUCT_INTEREST_OPTIONS.map((opt) => (
                    <option key={opt} value={opt} />
                  ))}
                </datalist>
              </div>
              <div>
                <label className="block text-[11px] text-white/40 mb-1">Lead source (optional)</label>
                <input
                  className="input text-sm w-full"
                  placeholder="Facebook Ads"
                  value={simLeadSource}
                  onChange={(e) => setSimLeadSource(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-[11px] text-white/40 mb-1">City (optional)</label>
                <input
                  className="input text-sm w-full"
                  placeholder="Monument"
                  value={simCity}
                  onChange={(e) => setSimCity(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-[11px] text-white/40 mb-1">State (optional)</label>
                <input
                  className="input text-sm w-full"
                  placeholder="CO"
                  value={simState}
                  onChange={(e) => setSimState(e.target.value)}
                />
              </div>
            </div>
          )}
        </div>

        {/* Running conversation — compact chat-bubble history. Each Generate
            appends the lead message + bot reply here so follow-ups are sent
            back to the API as conversationHistory. */}
        {conversationHistory.length > 0 && (
          <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
            {conversationHistory.map((turn, i) => (
              <div key={i} className={`flex ${turn.role === 'bot' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-xs px-3.5 py-2 rounded-2xl text-sm ${
                    turn.role === 'bot'
                      ? `rounded-tr-sm ${simulated ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20' : 'bg-civix-purple/20 text-white border border-civix-purple/30'}`
                      : 'rounded-tl-sm bg-civix-panelLight text-white/70'
                  }`}
                >
                  {turn.text}
                </div>
              </div>
            ))}
          </div>
        )}

        <form onSubmit={handleGenerate} className="flex gap-2">
          <input
            className="input flex-1"
            placeholder={isOpenerTurn ? 'Type the first inbound message…' : 'Type the lead’s next reply…'}
            value={inbound}
            onChange={(e) => setInbound(e.target.value)}
          />
          <button type="submit" disabled={generating || !inbound.trim()} className="btn-primary flex items-center gap-2 whitespace-nowrap">
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Generate
          </button>
        </form>

        {response !== null && (
          <div className="space-y-1">
            {simulated && (
              <div className="text-[10px] text-yellow-400/70 text-right">Simulated (no API key)</div>
            )}
            {reasoning && (
              <div className="text-[11px] text-white/30 text-right">{reasoning}</div>
            )}
          </div>
        )}
      </div>

      {/* Feedback panel — only show if there's a response */}
      {response !== null && isOwner && !feedbackDone && (
        <div className="panel p-5 space-y-4">
          {/* PR #623 — one-click training loop */}
          <div>
            <div className="text-sm font-medium text-white/80 mb-2">Rate this response</div>
            <div className="text-xs text-white/50 mb-3">
              <ThumbsUp className="w-3 h-3 inline mr-1 -mt-0.5" /> saves this exchange as a training example the bot will use next time.
              &nbsp;·&nbsp;
              <ThumbsDown className="w-3 h-3 inline mr-1 -mt-0.5" /> marks it bad; add a correction below and the bot will learn the right answer.
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={submitting}
                onClick={() => handleQuickFeedback('up')}
                className="flex items-center gap-2 px-3 py-1.5 rounded-sm border border-green-400/30 bg-green-400/10 text-green-300 hover:bg-green-400/20 transition-colors disabled:opacity-50"
                title="Save this exchange as a good training example"
              >
                <ThumbsUp className="w-4 h-4" /> Good
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={() => handleQuickFeedback('down')}
                className="flex items-center gap-2 px-3 py-1.5 rounded-sm border border-red-400/30 bg-red-400/10 text-red-300 hover:bg-red-400/20 transition-colors disabled:opacity-50"
                title={correction.trim() ? 'Save correction as training example' : 'Mark bad (no example added)'}
              >
                <ThumbsDown className="w-4 h-4" /> Bad
              </button>
              {submitting && <Loader2 className="w-4 h-4 animate-spin text-white/40" />}
            </div>
          </div>

          <div>
            <label className="block text-xs text-white/40 mb-1">Correction (optional — what should it have said?)</label>
            <textarea
              className="input w-full h-16 resize-none"
              placeholder="Type the ideal reply here — clicking Bad will save it as a training example"
              value={correction}
              onChange={(e) => setCorrection(e.target.value)}
            />
          </div>

          {/* Legacy 1-5 star + long-form feedback path preserved for the
              analyze-feedback loop. Collapsed by default; owner can still
              submit a numeric rating if they want it in the analysis. */}
          <details className="text-xs text-white/50">
            <summary className="cursor-pointer hover:text-white/70">More options: numeric rating</summary>
            <form onSubmit={handleFeedback} className="mt-3 space-y-3">
              <StarRating value={rating} onChange={setRating} />
              <button type="submit" disabled={submitting || !rating} className="btn-ghost flex items-center gap-2">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Submit Rating
              </button>
            </form>
          </details>
        </div>
      )}

      {feedbackDone && (
        <div className="flex items-center gap-2 text-civix-cyan text-sm">
          <CheckCircle2 className="w-4 h-4" /> Feedback recorded.
        </div>
      )}

      {/* Feedback pattern analysis */}
      {isOwner && (
        <div className="panel p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-white/80">Feedback Patterns</div>
              <div className="text-xs text-white/40 mt-0.5">
                {bot.feedback?.length || 0} entries — analyze to get personality improvement suggestions
              </div>
            </div>
            <button
              onClick={handleAnalyze}
              disabled={analyzing || !bot.feedback?.length}
              className="btn-ghost flex items-center gap-2"
            >
              {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lightbulb className="w-4 h-4" />}
              Review Patterns
            </button>
          </div>

          {proposed && (
            <div className="space-y-3">
              <div className="text-xs text-white/50 leading-relaxed">{proposed.reasoning}</div>
              <div className="grid grid-cols-2 gap-3">
                {proposed.personality && (
                  <div className="bg-civix-panelLight rounded-lg p-3 text-xs space-y-1">
                    <div className="text-white/60 font-medium">Proposed Personality</div>
                    <div>Tone: <span className="text-white">{proposed.personality.tone}</span></div>
                    <div>Formality: <span className="text-white">{proposed.personality.formality}</span></div>
                    <div>Emojis: <span className="text-white">{proposed.personality.useEmojis ? 'Yes' : 'No'}</span></div>
                    <div>Max Length: <span className="text-white">{proposed.personality.maxLength}</span></div>
                  </div>
                )}
                {proposed.compliance && (
                  <div className="bg-civix-panelLight rounded-lg p-3 text-xs space-y-1">
                    <div className="text-white/60 font-medium">Proposed Compliance</div>
                    <div>Opt-out: <span className="text-white">{proposed.compliance.includeOptOut ? 'On' : 'Off'}</span></div>
                    <div>Opt-out text: <span className="text-white">{proposed.compliance.optOutText}</span></div>
                  </div>
                )}
              </div>
              <button onClick={handleApply} disabled={applying} className="btn-primary flex items-center gap-2">
                {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Apply Changes
              </button>
            </div>
          )}
        </div>
      )}

      {/* Recent feedback list */}
      {recentFeedback.length > 0 && (
        <div className="panel p-5 space-y-3">
          <div className="text-sm font-medium text-white/80">Recent Feedback</div>
          <div className="space-y-2">
            {recentFeedback.map((f) => (
              <div key={String(f._id)} className="bg-civix-panelLight rounded-lg p-3 space-y-1.5 text-xs">
                <div className="flex items-center justify-between">
                  <StarRating value={f.rating} onChange={() => {}} />
                  <span className="text-white/30">{new Date(f.createdAt).toLocaleDateString()}</span>
                </div>
                <div className="text-white/60">In: <span className="text-white/80">{f.testInbound}</span></div>
                <div className="text-white/60">Generated: <span className="text-white/80">{f.generatedResponse}</span></div>
                {f.correction && (
                  <div className="text-civix-cyan">Correction: {f.correction}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function TextBotDetail() {
  const { id }  = useParams();
  const nav     = useNavigate();
  const qc      = useQueryClient();
  const [tab, setTab] = useState('scripts');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['text-bot', id],
    queryFn:  () => apiGet(`/text-bots/${id}`),
  });

  const bot     = data?.bot;
  const isOwner = data?._isOwner !== false;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 text-civix-purple animate-spin" />
      </div>
    );
  }
  if (!bot) {
    return (
      <div className="panel p-8 text-center text-white/50">
        Text bot not found.{' '}
        <button onClick={() => nav('/text-bots')} className="text-civix-cyan underline">Back</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <TopBar
        title={bot.name}
        subtitle={bot.description || 'AI Text Bot'}
        left={
          <button onClick={() => nav('/text-bots')} className="p-2 rounded-lg hover:bg-white/5 text-white/40 hover:text-white mr-2">
            <ArrowLeft className="w-5 h-5" />
          </button>
        }
        right={
          <span className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ${bot.autoReply ? 'bg-civix-cyan/15 text-civix-cyan' : 'bg-white/5 text-white/40'}`}>
            {bot.autoReply ? <Zap className="w-3 h-3" /> : <ZapOff className="w-3 h-3" />}
            Auto-reply {bot.autoReply ? 'ON' : 'OFF'}
          </span>
        }
      />

      {/* Stats row */}
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        <StatCard label="Scripts"   value={bot.scripts?.length || 0}          color="text-civix-violet" />
        <StatCard label="Examples"  value={bot.responseExamples?.length || 0} color="text-civix-cyan"   />
        <StatCard label="Feedback"  value={bot.feedback?.length || 0}          color="text-yellow-400"  />
        <StatCard label="Generated" value={bot.totalGenerations || 0}          color="text-civix-purple" />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto pb-1">
        {TABS.map((t) => (
          <TabBtn
            key={t.id}
            tab={t}
            active={tab === t.id}
            onClick={setTab}
            badge={t.id === 'test' && bot.feedback?.length > 0 ? 0 : 0}
          />
        ))}
      </div>

      {/* Tab content */}
      <div>
        {tab === 'scripts'     && <ScriptsTab     bot={bot} isOwner={isOwner} onRefresh={refetch} />}
        {tab === 'examples'    && <ExamplesTab    bot={bot} isOwner={isOwner} onRefresh={refetch} />}
        {tab === 'personality' && <PersonalityTab bot={bot} isOwner={isOwner} onRefresh={refetch} />}
        {tab === 'test'        && <TestTab        bot={bot} isOwner={isOwner} onRefresh={refetch} />}
      </div>
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="panel p-3 text-center">
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
      <div className="text-xs text-white/40 mt-0.5">{label}</div>
    </div>
  );
}
